import React, { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { TextField, Autocomplete } from './ui-kit';
import { FILTER_CONFIG } from '../utils/filterConstants';

const MultiSelectFilter = React.memo(function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  loading = false,
  error = false,
  disabled = false,
  placeholder = 'Select options',
}) {
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((option) =>
      typeof option === 'string' ? { value: option, label: option } : option
    );
  }, [options]);

  const selectedOptions = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return normalizedOptions.filter((option) => value.includes(option.value));
  }, [normalizedOptions, value]);

  const handleChange = useCallback(
    (_event, newValue) => {
      onChange(newValue.map((item) => item.value));
    },
    [onChange]
  );

  if (loading) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading {label.toLowerCase()}...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Failed to load {label.toLowerCase()}
        </Typography>
      </Box>
    );
  }

  if (normalizedOptions.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No {label.toLowerCase()} available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {label}
      </Typography>
      <Autocomplete
        multiple
        options={normalizedOptions}
        value={selectedOptions}
        onChange={handleChange}
        disableCloseOnSelect
        disabled={disabled}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={value?.length > 0 ? '' : placeholder}
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                minHeight: FILTER_CONFIG.INPUT_HEIGHT,
                height: 'auto',
                alignItems: 'flex-start',
                paddingTop: '6px',
                paddingBottom: '6px',
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.label}
              size="small"
              {...getTagProps({ index })}
              key={option.value}
            />
          ))
        }
        size="small"
        sx={{ width: '100%' }}
      />
    </Box>
  );
});

export default MultiSelectFilter;
